import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import styles from "./RowActionsMenu.module.css";

export default function RowActionsMenu({ actions = [], align = "right" }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: -9999, left: -9999 });

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !menuRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const menuWidth = menuRef.current.offsetWidth;
    const spaceBelow = window.innerHeight - rect.bottom - 4;
    const openUpward = spaceBelow < menuHeight && rect.top > menuHeight;
    const left =
      align === "left"
        ? rect.left
        : Math.max(8, rect.right - menuWidth);
    setMenuPos({
      top: openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4,
      left,
    });
  }, [align]);

  const menuCallbackRef = useCallback(
    (node) => {
      menuRef.current = node;
      if (node) updatePosition();
    },
    [updatePosition]
  );

  useEffect(() => {
    if (!open) {
      setMenuPos({ top: -9999, left: -9999 });
      return;
    }
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  if (!actions.length) return null;

  const menu = open
    ? ReactDOM.createPortal(
        <div
          ref={menuCallbackRef}
          className={styles.menu}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{ position: "fixed", top: menuPos.top, left: menuPos.left }}
          role="menu"
        >
          {actions.map((action, idx) => (
            <button
              key={idx}
              type="button"
              className={`${styles.item} ${action.danger ? styles.itemDanger : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                action.onClick?.(e);
              }}
              role="menuitem"
            >
              {action.label}
            </button>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={styles.trigger}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {menu}
    </>
  );
}
